import React, { useState } from 'react';
import { resolveAvatarUrl } from '../api/baseURL';

interface AvatarProps {
  /** Picture URL; falls back to an initial when empty. */
  src?: string | null;
  /** Name (or email) the initial is taken from. */
  name?: string | null;
  /** Diameter in pixels. */
  size?: number;
}

/**
 * A user's picture, or the first letter of their name, in a fixed circle.
 *
 * Every render site puts the avatar in a flex row next to a name that can be
 * long, and on a phone the row is narrow. A plain box with width and height
 * set is still a flex item that shrinks: it was squeezed to the width of its
 * one letter and became a tall oval (issue #319). The circle here refuses to
 * shrink, and a picture is cropped to the circle rather than stretched.
 *
 * A picture that does not load shows the initial instead of an empty
 * circle: the API answers 404 for an uploaded picture to anyone who shares
 * no workspace with its owner (the picture of a project member from outside
 * the workspace, say), as it does for a picture that is gone.
 */
export const Avatar: React.FC<AvatarProps> = ({ src, name, size = 28 }) => {
  // The URL that failed, so that another picture is tried afresh.
  const [failed, setFailed] = useState('');
  const box: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    borderRadius: '50%',
    flexShrink: 0,
    boxSizing: 'border-box',
  };

  const resolved = resolveAvatarUrl(src);
  if (resolved && resolved !== failed) {
    return (
      <img
        src={resolved}
        alt=""
        onError={() => setFailed(resolved)}
        style={{ ...box, objectFit: 'cover', display: 'block' }}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      style={{
        ...box,
        background: 'var(--accent)',
        color: 'var(--accent-fg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.46),
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
};
